import { compact } from 'lodash'
import debounce from 'lodash.debounce'
import React, { useCallback, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'

import { usePageTitle } from '../../hooks/usePageTitle'
import { Team } from '../../models/Team'
import { User } from '../../models/User'
import { getTeamName } from '../../utils/teams'
import { Spinner } from '../Spinner'
import { UserHeader } from '../app/UserHeader'
import { ThreadHeader } from './ThreadHeader'
import { MessageBubble } from './MessageBubble'
import { ConversationState, InboxState } from './MessagesPage'

interface ConversationThreadViewProps {
    conversationState: ConversationState
    inboxState: InboxState
    onLoadMore: () => void
}

export const ConversationView: React.FC<ConversationThreadViewProps> = ({
    conversationState,
    inboxState,
    onLoadMore,
}) => {
    const { isLoading, currentThread, messages, error, pagination } = conversationState
    const { hasMore, justScrolled } = pagination
    const { isSending, inbox, unreadCount } = inboxState

    const messageListRef = useRef<HTMLDivElement>(null)
    const lastMessageRef = useRef<HTMLSpanElement>(null)

    const handleScroll = debounce(() => {
        if (!messageListRef.current) return
        const { scrollTop, scrollHeight, offsetHeight } = messageListRef.current
        const percentToTop = scrollTop / (scrollHeight - offsetHeight)
        if (hasMore && percentToTop < 0.2 && !isLoading) {
            console.log('Load more')
            onLoadMore()
        }
        if (scrollTop === 0) {
            // Hack around the issue of loading more messages causes us to lose our scroll
            // position when we are at 0. It jumps to the latest messages.
            messageListRef.current.scrollTop = 1
        }
    }, 200)

    // Automatically load more conversations when the scroll bar is near the top
    useEffect(() => {
        const messageList = messageListRef.current
        if (messageList) {
            messageList.addEventListener('scroll', handleScroll)
        }
        return () => {
            if (messageList) {
                messageList.removeEventListener('scroll', handleScroll)
            }
        }
    }, [handleScroll])

    // Scrolling to bottom when the chat loads or after we send/receive a message,
    // but not when adding older messages due to scrolling
    useEffect(() => {
        // justScrolled indicates the messages were loaded via loadMore
        if (lastMessageRef.current && !justScrolled) {
            lastMessageRef.current.scrollIntoView({ behavior: 'auto' })
        }
    }, [messages.length, justScrolled])

    const isGroup = currentThread instanceof Team

    let threadName: string | undefined
    let url = ''

    if (isGroup) {
        url = '#'
        threadName = getTeamName(currentThread)
    }
    else if (currentThread) {
        url = `/clients/${currentThread.id}`
        threadName = (currentThread as User).name || ''
    }

    usePageTitle(compact([threadName, 'Messages']).join(' '))

    return (
        <div className="flex min-h-0 grow flex-col items-stretch justify-start">
            <ThreadHeader
                title={
                    threadName ? (
                        <Link
                            style={{
                                paddingTop: '23px',
                                paddingLeft: '24px',
                                lineHeight: '24px',
                                fontSize: '14px',
                                color: '#3D84F6',
                                fontWeight: 700,
                                textDecoration: 'none',
                                display: 'flex',
                            }}
                            to={url}
                        >
                            {threadName} <span style={{ marginLeft: '6px' }}>&rarr;</span>
                        </Link>
                    ) : undefined
                }
                trailingWidget={<UserHeader />}
            />

            {error && <div style={{ color: 'red', fontWeight: 700 }}>{error}</div>}

            {isLoading ? (
                <div
                    style={{
                        position: 'absolute',
                        marginLeft: '-64px',
                        width: 'calc(100% - 640px)',
                        transform: 'translate(50%, 50%)',
                    }}
                >
                    <Spinner />
                </div>
            ) : !isSending && !currentThread ? (
                inbox.length === 0 ? (
                    <div className="emptyState">
                        <div>Send a message to get started 💬</div>
                    </div>
                ) : (
                    <div className="emptyState grow">
                        <div>Select a message to get started</div>
                        <div className="unread">
                            You have {unreadCount} unread message{unreadCount !== 1 ? 's' : ''} 💬
                        </div>
                    </div>
                )
            ) : null}
            {currentThread && (
                <div
                    id="messageList"
                    ref={messageListRef}
                    className="w-full grow overflow-y-auto overscroll-contain"
                >
                    {messages.length === 0 && (
                        <div className="helperText">
                            {isGroup && currentThread.bcc ? (
                                <>
                                    Your message to {threadName} will be delivered to their phones
                                    via regular SMS text from your dedicated business number.
                                    <br />
                                    <br />
                                    These clients will receive your message as Bcc, meaning client
                                    replies will be sent privately to you.
                                    <br />
                                    <br />
                                </>
                            ) : (
                                ''
                            )}
                        </div>
                    )}
                    {messages
                        .slice()
                        .reverse()
                        .map((message) => (
                            <MessageBubble key={`message-${message.id}`} message={message} />
                        ))}
                    <span
                        ref={lastMessageRef}
                        style={{ float: 'right', width: '1px', clear: 'both' }}
                    />
                </div>
            )}
        </div>
    )
}
